// ============================================================================
// HCAI Reporting Tool - Zod Validation Schemas
// Form validation schemas for all data entry forms
// ============================================================================

import { z } from 'zod';

// ============================================================================
// Device Snapshot Form Schema
// ============================================================================

export const deviceSnapshotSchema = z.object({
  wardId: z.string().min(1, 'Ward is required'),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
  cvp: z.number()
    .min(0, 'CVP count must be at least 0')
    .max(1000, 'CVP count cannot exceed 1000')
    .int('CVP count must be a whole number'),
  pl: z.number()
    .min(0, 'PL count must be at least 0')
    .max(1000, 'PL count cannot exceed 1000')
    .int('PL count must be a whole number'),
  urinaryCatheter: z.number()
    .min(0, 'Urinary catheter count must be at least 0')
    .max(1000, 'Urinary catheter count cannot exceed 1000')
    .int('Urinary catheter count must be a whole number'),
  ventilator: z.number()
    .min(0, 'Ventilator count must be at least 0')
    .max(1000, 'Ventilator count cannot exceed 1000')
    .int('Ventilator count must be a whole number'),
  // Device insertion dates (optional)
  cvpInsertionDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format').optional().or(z.literal('')),
  plInsertionDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format').optional().or(z.literal('')),
  urinaryCatheterInsertionDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format').optional().or(z.literal('')),
  ventilatorInsertionDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format').optional().or(z.literal('')),
  // Auto-calculated days (optional)
  cvpDays: z.number().optional(),
  plDays: z.number().optional(),
  urinaryCatheterDays: z.number().optional(),
  ventilatorDays: z.number().optional(),
}).refine((data) => {
  // Validate insertion dates are not after recording date
  const recordingDate = new Date(data.date);

  const validateDate = (insertionDate?: string) => {
    if (!insertionDate) return true;
    const insertion = new Date(insertionDate);
    return insertion <= recordingDate;
  };

  const isValid =
    validateDate(data.cvpInsertionDate) &&
    validateDate(data.plInsertionDate) &&
    validateDate(data.urinaryCatheterInsertionDate) &&
    validateDate(data.ventilatorInsertionDate);

  return isValid;
}, {
  message: 'Device insertion date cannot be after the recording date',
});

export type DeviceSnapshotFormData = z.infer<typeof deviceSnapshotSchema>;

// ============================================================================
// HCAI Record Form Schema
// ============================================================================

export const hcaiRecordSchema = z.object({
  wardId: z.string().min(1, 'Ward is required'),
  periodStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
  periodEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
  uti: z.number()
    .min(0, 'CAUTI cases must be at least 0')
    .max(1000, 'CAUTI cases cannot exceed 1000')
    .int('CAUTI cases must be a whole number'),
  ssiSuperficial: z.number()
    .min(0, 'SSI Superficial cases must be at least 0')
    .max(1000, 'SSI Superficial cases cannot exceed 1000')
    .int('SSI Superficial cases must be a whole number'),
  ssiDeep: z.number()
    .min(0, 'SSI Deep cases must be at least 0')
    .max(1000, 'SSI Deep cases cannot exceed 1000')
    .int('SSI Deep cases must be a whole number'),
  ssiOrganSpace: z.number()
    .min(0, 'SSI Organ Space cases must be at least 0')
    .max(1000, 'SSI Organ Space cases cannot exceed 1000')
    .int('SSI Organ Space cases must be a whole number'),
  hapOther: z.number()
    .min(0, 'HAP Other cases must be at least 0')
    .max(1000, 'HAP Other cases cannot exceed 1000')
    .int('HAP Other cases must be a whole number'),
  vap: z.number()
    .min(0, 'VAP cases must be at least 0')
    .max(1000, 'VAP cases cannot exceed 1000')
    .int('VAP cases must be a whole number'),
  bsiOther: z.number()
    .min(0, 'BSI Other cases must be at least 0')
    .max(1000, 'BSI Other cases cannot exceed 1000')
    .int('BSI Other cases must be a whole number'),
  cabsi: z.number()
    .min(0, 'CLABSI cases must be at least 0')
    .max(1000, 'CLABSI cases cannot exceed 1000')
    .int('CLABSI cases must be a whole number'),
  sepsis: z.number()
    .min(0, 'Sepsis cases must be at least 0')
    .max(1000, 'Sepsis cases cannot exceed 1000')
    .int('Sepsis cases must be a whole number'),
  other: z.number()
    .min(0, 'Other cases must be at least 0')
    .max(1000, 'Other cases cannot exceed 1000')
    .int('Other cases must be a whole number'),
  notes: z.string().max(1000, 'Notes cannot exceed 1000 characters').optional(),
}).refine((data) => {
  const start = new Date(data.periodStart);
  const end = new Date(data.periodEnd);
  return end >= start;
}, {
  message: 'End date must be after or equal to start date',
  path: ['periodEnd'],
});

export type HCAIRecordFormData = z.infer<typeof hcaiRecordSchema>;
// Backward compatibility alias
export type HAIRecordFormData = HCAIRecordFormData;

// ============================================================================
// SSI Surveillance Form Schema
// ============================================================================

export const ssiSurveillanceSchema = z.object({
  wardId: z.string().min(1, 'Ward is required'),
  periodStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
  periodEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
  procedures: z.number()
    .min(0, 'Procedures must be at least 0')
    .max(10000, 'Procedures cannot exceed 10000')
    .int('Procedures must be a whole number'),
  infections: z.number()
    .min(0, 'Infections must be at least 0')
    .max(10000, 'Infections cannot exceed 10000')
    .int('Infections must be a whole number'),
  notes: z.string().max(1000, 'Notes cannot exceed 1000 characters').optional(),
}).refine((data) => {
  const start = new Date(data.periodStart);
  const end = new Date(data.periodEnd);
  return end >= start;
}, {
  message: 'End date must be after or equal to start date',
  path: ['periodEnd'],
}).refine((data) => {
  return data.infections <= data.procedures;
}, {
  message: 'Infections cannot exceed total procedures',
  path: ['infections'],
});

export type SSISurveillanceFormData = z.infer<typeof ssiSurveillanceSchema>;

// ============================================================================
// Patient-Level Data Entry Form Schema
// ============================================================================

export const patientDeviceSchema = z.object({
  deviceType: z.enum(['cvp', 'pl', 'urinaryCatheter', 'ventilator', 'cSection', 'surgery']),
  insertionDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
});

export const patientHCAICaseSchema = z.object({
  hcaiType: z.enum(['uti', 'ssiSuperficial', 'ssiDeep', 'ssiOrganSpace', 'hapOther', 'vap', 'bsiOther', 'cabsi', 'sepsis', 'other']),
  detectionDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
  detectionMethod: z.enum(['clinical', 'laboratory']),
  ssiType: z.enum(['superficial', 'deep', 'organSpace']).optional(),
  pneumoniaType: z.enum(['vap', 'hapOther']).optional(),
  bsiType: z.enum(['cabsi', 'bsiOther']).optional(),
  associatedDeviceType: z.enum(['cvp', 'pl', 'urinaryCatheter', 'ventilator', 'cSection', 'surgery']).optional(),
  notes: z.string().max(500, 'Notes cannot exceed 500 characters').optional(),
});

// Backward compatibility alias
export const patientHAICaseSchema = patientHCAICaseSchema;

export const patientDetailFormSchema = z.object({
  wardName: z.string()
    .min(1, 'Ward name is required')
    .max(100, 'Ward name cannot exceed 100 characters'),
  patientIdentifier: z.string().min(1, 'Patient ID is required'),
  gender: z.enum(['M', 'F', 'O']).optional(),
  age: z.number().min(0, 'Age must be at least 0').max(150, 'Age cannot exceed 150').optional(),
  admissionDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
  recordDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
  dischargeDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format').optional().or(z.literal('')),
  clinicalDiagnosis: z.string().max(500, 'Clinical diagnosis cannot exceed 500 characters').optional(),
  riskFactors: z.array(z.enum(['underlyingDisease', 'immunosuppressiveTherapy', 'prolongedHospitalization', 'prematurity', 'surgicalProcedure', 'other'])).optional(),
  devices: z.array(patientDeviceSchema).min(0, 'Device list'),
  hcaiCases: z.array(patientHCAICaseSchema).min(0, 'HCAI cases list'),
  outcome: z.enum(['admitted', 'discharged', 'deceased', 'transferred']).optional(),
  outcomeDatetime: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  transferredFacility: z.string().max(200, 'Transferred facility cannot exceed 200 characters').optional(),
}).refine((data) => {
  // Validate discharge date is after admission date
  if (data.dischargeDate) {
    const admission = new Date(data.admissionDate);
    const discharge = new Date(data.dischargeDate);
    return discharge >= admission;
  }
  return true;
}, {
  message: 'Discharge date must be after or equal to admission date',
  path: ['dischargeDate'],
}).refine((data) => {
  // Validate record date is after or equal to admission date
  const admission = new Date(data.admissionDate);
  const recordDate = new Date(data.recordDate);
  return recordDate >= admission;
}, {
  message: 'Record date must be after or equal to admission date',
  path: ['recordDate'],
}).refine((data) => {
  // Validate device insertion dates are after or equal to admission date
  const admissionDate = new Date(data.admissionDate);
  const recordDate = new Date(data.recordDate);

  for (const device of data.devices) {
    const insertionDate = new Date(device.insertionDate);

    // Device insertion must be after admission
    if (insertionDate < admissionDate) {
      return false;
    }

    // Device insertion must be before record date
    if (insertionDate > recordDate) {
      return false;
    }
  }

  return true;
}, {
  message: 'Device insertion date must be between admission and record date',
  path: ['devices'],
}).refine((data) => {
  // Validate HCAI detection dates are within admission period
  const admissionDate = new Date(data.admissionDate);
  const recordDate = new Date(data.recordDate);

  for (const hcaiCase of data.hcaiCases) {
    const detectionDate = new Date(hcaiCase.detectionDate);

    // Detection must be after admission
    if (detectionDate < admissionDate) {
      return false;
    }

    // Detection must be before or on record date
    if (detectionDate > recordDate) {
      return false;
    }
  }

  return true;
}, {
  message: 'HCAI detection date must be between admission and record date',
  path: ['hcaiCases'],
}).refine((data) => {
  // Validate: cannot record HCAI without at least one device
  if (data.hcaiCases.length > 0 && data.devices.length === 0) {
    return false;
  }
  return true;
}, {
  message: 'Cannot record infections without at least one device. Please add a device first.',
  path: ['hcaiCases'],
}).refine((data) => {
  // Validate device-infection type matching and count
  const deviceCounts = {
    cvp: data.devices.filter(d => d.deviceType === 'cvp').length,
    pl: data.devices.filter(d => d.deviceType === 'pl').length,
    urinaryCatheter: data.devices.filter(d => d.deviceType === 'urinaryCatheter').length,
    ventilator: data.devices.filter(d => d.deviceType === 'ventilator').length,
    cSection: data.devices.filter(d => d.deviceType === 'cSection').length,
    surgery: data.devices.filter(d => d.deviceType === 'surgery').length,
  };

  const hcaiCounts = {
    uti: data.hcaiCases.filter(h => h.hcaiType === 'uti').length,
    ssiSuperficial: data.hcaiCases.filter(h => h.hcaiType === 'ssiSuperficial').length,
    ssiDeep: data.hcaiCases.filter(h => h.hcaiType === 'ssiDeep').length,
    ssiOrganSpace: data.hcaiCases.filter(h => h.hcaiType === 'ssiOrganSpace').length,
    hapOther: data.hcaiCases.filter(h => h.hcaiType === 'hapOther').length,
    vap: data.hcaiCases.filter(h => h.hcaiType === 'vap').length,
    bsiOther: data.hcaiCases.filter(h => h.hcaiType === 'bsiOther').length,
    cabsi: data.hcaiCases.filter(h => h.hcaiType === 'cabsi').length,
    sepsis: data.hcaiCases.filter(h => h.hcaiType === 'sepsis').length,
    other: data.hcaiCases.filter(h => h.hcaiType === 'other').length,
  };

  // CAUTI is associated with urinary catheters
  if (hcaiCounts.uti > deviceCounts.urinaryCatheter) {
    return false;
  }

  // CLABSI is associated with CVP or PL
  if (data.hcaiCases.some(h => h.hcaiType === 'cabsi' && deviceCounts.cvp === 0 && deviceCounts.pl === 0)) {
    return false;
  }

  // VAP is only associated with ventilators
  if (hcaiCounts.vap > deviceCounts.ventilator) {
    return false;
  }

  // SSI types are associated with surgical procedures
  const totalSsi = hcaiCounts.ssiSuperficial + hcaiCounts.ssiDeep + hcaiCounts.ssiOrganSpace;
  const totalSurgical = deviceCounts.cSection + deviceCounts.surgery;
  if (totalSsi > totalSurgical) {
    return false;
  }

  return true;
}, {
  message: 'Infection types must match their associated devices: CAUTI with Catheters, CLABSI with CVP/PL, VAP with Ventilators, SSI types with Surgical Procedures',
  path: ['hcaiCases'],
});

export type PatientHCAICaseFormData = z.infer<typeof patientHCAICaseSchema>;
// Backward compatibility alias
export type PatientHAICaseFormData = PatientHCAICaseFormData;

export type PatientDetailFormData = z.infer<typeof patientDetailFormSchema>;

// ============================================================================
// Ward Form Schema (for admin)
// ============================================================================

export const wardSchema = z.object({
  name: z.string()
    .min(2, 'Ward name must be at least 2 characters')
    .max(100, 'Ward name cannot exceed 100 characters'),
  description: z.string()
    .max(500, 'Description cannot exceed 500 characters')
    .optional(),
  facilityId: z.string().min(1, 'Facility is required'),
  isActive: z.boolean().default(true),
});

export type WardFormData = z.infer<typeof wardSchema>;

// ============================================================================
// Facility Form Schema (for admin)
// ============================================================================

export const facilitySchema = z.object({
  name: z.string()
    .min(2, 'Facility name must be at least 2 characters')
    .max(200, 'Facility name cannot exceed 200 characters'),
  region: z.string()
    .min(2, 'Region must be at least 2 characters')
    .max(100, 'Region cannot exceed 100 characters'),
  isActive: z.boolean().default(true),
});

export type FacilityFormData = z.infer<typeof facilitySchema>;

// ============================================================================
// User Management Form Schema (for admin)
// ============================================================================

export const userUpdateSchema = z.object({
  displayName: z.string()
    .min(2, 'Display name must be at least 2 characters')
    .max(100, 'Display name cannot exceed 100 characters'),
  role: z.enum(['Admin', 'IPC_Nurse', 'Ward_Nurse', 'Officer']),
  facilityId: z.string().min(1, 'Facility is required'),
  isActive: z.boolean(),
});

export type UserUpdateFormData = z.infer<typeof userUpdateSchema>;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format validation error messages for display
 */
export function formatZodError(error: z.ZodError): Record<string, string> {
  const errors: Record<string, string> = {};

  error.errors.forEach((err) => {
    const path = err.path.join('.');
    errors[path] = err.message;
  });

  return errors;
}

/**
 * Get today's date in YYYY-MM-DD format
 */
export function getTodayString(): string {
  const today = new Date();
  return today.toISOString().split('T')[0];
}

/**
 * Get date N days ago in YYYY-MM-DD format
 */
export function getDaysAgoString(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split('T')[0];
}

/**
 * Validate date is not in the future
 */
export function validateNotFuture(dateString: string): boolean {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(23, 59, 59, 999); // End of today
  return date <= today;
}

/**
 * Validate date is within acceptable range (e.g., last 365 days)
 */
export function validateDateRange(dateString: string, maxDaysAgo: number = 365): boolean {
  const date = new Date(dateString);
  const minDate = new Date();
  minDate.setDate(minDate.getDate() - maxDaysAgo);
  const today = new Date();

  return date >= minDate && date <= today;
}

// ============================================================================
// Comprehensive HCAI Surveillance Form Schema (Official Form)
// ============================================================================

export const deviceRecordSchema = z.object({
  deviceType: z.enum(['indwellingUrinaryCatheter', 'mechanicalVentilation', 'tracheostomy', 'centralVenousCatheter', 'arterialLine', 'peripheralVenousLine', 'otherDrainageCatheters']),
  insertionDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
  removalDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)').optional().or(z.literal('')),
});

export const antibioticRecordSchema = z.object({
  name: z.string().min(1, 'Antibiotic name is required').max(100, 'Name cannot exceed 100 characters'),
  group: z.string().max(100, 'Group cannot exceed 100 characters').optional(),
  indication: z.enum(['empirical', 'therapeutic', 'prophylaxis']),
  dateCommenced: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
  routeOfAdmission: z.string().max(100, 'Route cannot exceed 100 characters'),
  duration: z.number().min(0, 'Duration must be at least 0').int('Duration must be a whole number'),
  meetsLocalPolicy: z.enum(['Y', 'N', 'NK']).optional(),
});

export const microbiologicalRecordSchema = z.object({
  collectionDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
  labReportDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)').optional().or(z.literal('')),
  specimenType: z.string().min(1, 'Specimen type is required').max(100, 'Type cannot exceed 100 characters'),
  organismIsolated: z.string().max(200, 'Organism cannot exceed 200 characters').optional(),
  antibiographyAttached: z.boolean().optional(),
});

export const ssiOutcomeSchema = z.object({
  detectionMethod: z.enum(['clinical', 'laboratory']).optional().or(z.literal('')),
  type: z.enum(['superficial', 'deep', 'organSpace']).optional().or(z.literal('')),
  woundType: z.enum(['clean', 'cleanContaminated', 'contaminated', 'dirtyInfected']).optional().or(z.literal('')),
  onsetDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)').optional().or(z.literal('')),
});

export const infectionOutcomeSchema = z.object({
  infectionType: z.enum(['ssi', 'uti', 'pneumonia', 'bsi', 'sepsis', 'other']).or(z.literal('')).optional(),
  detectionMethod: z.enum(['clinical', 'laboratory']).or(z.literal('')).optional(),
  onsetDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)').optional().or(z.literal('')),
  // For pneumonia
  pneumoniaType: z.enum(['hapOther', 'vap']).or(z.literal('')).optional(),
  // For BSI
  bsiType: z.enum(['cabsi', 'bsiOther']).or(z.literal('')).optional(),
  // For SSI
  ssi: ssiOutcomeSchema.optional(),
  // For other infections
  otherSpecify: z.string().max(200, 'Specification cannot exceed 200 characters').optional(),
});

export const hcaiSurveillanceFormSchema = z.object({
  // 1. CASE IDENTIFICATION DATA
  district: z.string().max(100, 'District cannot exceed 100 characters').optional(),
  subDistrict: z.string().max(100, 'Sub-District cannot exceed 100 characters').optional(),
  hospital: z.string().max(200, 'Hospital cannot exceed 200 characters').optional(),
  department: z.string().max(100, 'Department cannot exceed 100 characters').optional(),
  patientName: z.string().max(200, 'Patient name cannot exceed 200 characters').optional(),
  hospitalNumber: z.string().max(50, 'Hospital number cannot exceed 50 characters').optional(),
  transferredIn: z.boolean().optional(),
  transferringFacility: z.string().max(200, 'Facility name cannot exceed 200 characters').optional(),
  gender: z.enum(['M', 'F']).optional(),
  age: z.number().min(0, 'Age must be at least 0').max(150, 'Age cannot exceed 150').int('Age must be a whole number').optional(),
  ward: z.string().max(100, 'Ward name cannot exceed 100 characters').optional(),
  admissionDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)').optional(),

  // 2. CLINICAL DATA
  clinicalDiagnosis: z.string().max(500, 'Diagnosis cannot exceed 500 characters').optional(),

  // Risk factors
  riskFactors: z.object({
    underlyingDisease: z.boolean().optional(),
    underlyingDiseaseSpec: z.string().max(200, 'Specification cannot exceed 200 characters').optional(),
    immunosuppressiveTherapy: z.boolean().optional(),
    prolongedHospitalization: z.boolean().optional(),
    prematurity: z.boolean().optional(),
    surgicalProcedure: z.boolean().optional(),
    surgicalProcedureType: z.string().max(200, 'Type cannot exceed 200 characters').optional(),
    others: z.boolean().optional(),
    othersSpec: z.string().max(200, 'Specification cannot exceed 200 characters').optional(),
  }).optional(),

  // 2.3 Devices
  devices: z.array(deviceRecordSchema).optional(),

  // 2.4 Antibiotic therapy
  antibiotics: z.array(antibioticRecordSchema).optional(),

  // 3. MICROBIOLOGICAL DATA
  microbiological: z.array(microbiologicalRecordSchema).optional(),

  // 4. OUTCOME DATA
  infections: z.array(infectionOutcomeSchema).optional(),

  // Patient outcome
  patientOutcome: z.enum(['admitted', 'discharged', 'deceased', 'transferred']).optional(),
  outcomeDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)').optional().or(z.literal('')),
  transferredToFacility: z.string().max(200, 'Facility cannot exceed 200 characters').optional(),

  // Reporting details
  reportedBy: z.string().max(100, 'Name cannot exceed 100 characters').optional(),
  reporterRank: z.string().max(100, 'Rank cannot exceed 100 characters').optional(),
  reportDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)').optional().or(z.literal('')),
}).refine((data) => {
  if (data.admissionDate && data.outcomeDate && data.outcomeDate !== '') {
    const admission = new Date(data.admissionDate);
    const outcome = new Date(data.outcomeDate);
    return outcome >= admission;
  }
  return true;
}, {
  message: 'Outcome date must be after or equal to admission date',
  path: ['outcomeDate'],
}).refine((data) => {
  if (data.transferredIn && !data.transferringFacility) {
    return false;
  }
  return true;
}, {
  message: 'Transferring facility must be specified if transferred in',
  path: ['transferringFacility'],
}).refine((data) => {
  if (data.patientOutcome === 'transferred' && !data.transferredToFacility) {
    return false;
  }
  return true;
}, {
  message: 'Transferred facility must be specified if outcome is transferred',
  path: ['transferredToFacility'],
});

export type HCAISurveillanceFormData = z.infer<typeof hcaiSurveillanceFormSchema>;
